import React from 'react';
import { Tabs, TabsList, TabsTrigger } from '../../ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../../ui/popover';
import { Calendar as CalendarComponent } from '../../ui/calendar';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '../../../lib/utils';

const GeneratePage = ({
  generateTableTab,
  setGenerateTableTab,
  payrollPeriod,
  setPayrollPeriod,
  mfAttendanceFilters,
  setMfAttendanceFilters,
  payrollAttendanceFilters,
  setPayrollAttendanceFilters,
  holidays,
  setHolidays,
  generateBatchPayslips,
  isLoadingMfAttendance,
  isLoadingPayrollAttendance,
  isSavingPayrollBatch,
  saveAllPayrollPayslipByPeriod,
  generatedPayslips,
  setGeneratedPayslips,
  setIsClearingMfAttendance,
  setIsLoadingMfAttendance,
  setMfAttendanceData,
  setRawMfAttendanceData,
  setMfAttendanceAnalytics,
  setIsClearingPayrollAttendance,
  setIsLoadingPayrollAttendance,
  setPayrollAttendanceData,
  setRawPayrollAttendanceData,
  setPayrollAttendanceAnalytics,
  setSelectedPayslipEmployee,
  isClearingMfAttendance,
  isClearingPayrollAttendance,
  setIsPayslipModalOpen,
  fetchAndCalculatePayslipMf,
  fetchAndCalculatePayslip,
  computeDateRangeMonthCount,
  normalizeDateRangeOrder,
  selectedGeneratedPayslipEmployeeId,
  handleGeneratedPayslipClick,
  isGeneratedPayslipSummaryOpen,
  setIsGeneratedPayslipSummaryOpen,
  selectedGeneratedPayslipEmployeeName,
  renderPayslipCalculationSummary
}) => (
  <div className="grid gap-6 h-[calc(100vh-180px)] min-h-[640px]" style={{ gridTemplateRows: '30% 70%' }}>
    <div className="grid grid-cols-1 lg:grid-cols-[3fr_7fr] gap-6 min-h-0">
      <Card className="glass shadow-lg border-0 sticky top-0 z-20 bg-white/90 backdrop-blur h-full min-h-0">
        <CardHeader>
          <div className="flex items-center justify-start gap-3">
            <Tabs value={generateTableTab} onValueChange={setGenerateTableTab}>
              <TabsList>
                <TabsTrigger value="payroll">Payroll</TabsTrigger>
                <TabsTrigger value="mf">MF</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardHeader>
        <CardContent>
          <div key={generateTableTab} className="animate-in fade-in slide-in-from-bottom-2 duration-300 ease-in-out">
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label>Payroll Period</Label>
                <Input
                  placeholder="e.g., 1st Half of January 2025"
                  value={payrollPeriod}
                  onChange={(e) => setPayrollPeriod(e.target.value)}
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date}
                    onChange={(e) => {
                      const start = e.target.value;
                      if (generateTableTab === 'mf') {
                        const end = mfAttendanceFilters.end_date || start;
                        const norm = normalizeDateRangeOrder(start, end);
                        setMfAttendanceFilters(({ ...mfAttendanceFilters, start_date: norm.start, end_date: norm.end }));
                      } else {
                        const end = payrollAttendanceFilters.end_date || start;
                        const norm = normalizeDateRangeOrder(start, end);
                        setPayrollAttendanceFilters(({ ...payrollAttendanceFilters, name: '', start_date: norm.start, end_date: norm.end }));
                      }
                    }}
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date}
                    onChange={(e) => {
                      const end = e.target.value;
                      if (generateTableTab === 'mf') {
                        const start = mfAttendanceFilters.start_date || end;
                        const norm = normalizeDateRangeOrder(start, end);
                        setMfAttendanceFilters(({ ...mfAttendanceFilters, start_date: norm.start, end_date: norm.end }));
                      } else {
                        const start = payrollAttendanceFilters.start_date || end;
                        const norm = normalizeDateRangeOrder(start, end);
                        setPayrollAttendanceFilters(({ ...payrollAttendanceFilters, name: '', start_date: norm.start, end_date: norm.end }));
                      }
                    }}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Holidays</Label>
                </div>

                <div className="space-y-2">
                  {holidays.map((h) => (
                    <div key={h.id} className="grid grid-cols-[1fr_auto] gap-2 items-center">
                      <Input
                        placeholder="Holiday name"
                        value={h.name || ''}
                        onChange={(e) => {
                          const nextName = e.target.value;
                          setHolidays((prev) => prev.map((x) => x.id === h.id ? { ...x, name: nextName } : x));
                        }}
                      />
                      <Popover>
                        <PopoverTrigger asChild>
                          <Button variant="outline" size="icon" aria-label="Pick holiday date">
                            <CalendarIcon className="h-4 w-4" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="p-2 w-auto" align="end">
                          <CalendarComponent
                            mode="single"
                            selected={h.date ? new Date(h.date) : undefined}
                            defaultMonth={h.date ? new Date(h.date) : undefined}
                            onSelect={(d) => {
                              const nextDate = d ? format(d, 'yyyy-MM-dd') : '';
                              setHolidays((prev) => prev.map((x) => x.id === h.id ? { ...x, date: nextDate } : x));
                            }}
                            className="p-2"
                            classNames={{
                              day_selected: 'bg-green-600 text-white hover:bg-green-600 hover:text-white focus:bg-green-600 focus:text-white',
                              day_today: 'bg-green-50 text-green-900'
                            }}
                          />
                        </PopoverContent>
                      </Popover>
                    </div>
                  ))}

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setHolidays((prev) => ([...prev, { id: Date.now() + Math.random(), name: '', date: '' }]));
                    }}
                    className="w-full"
                  >
                    +
                  </Button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 mt-4">
              <Button
                onClick={() => {
                  generateBatchPayslips();
                }}
                disabled={generateTableTab === 'mf' ? isLoadingMfAttendance : isLoadingPayrollAttendance}
                className="bg-indigo-600 hover:bg-indigo-700"
              >
                {(generateTableTab === 'mf' ? isLoadingMfAttendance : isLoadingPayrollAttendance) ? 'Loading...' : 'Generate Payroll'}
              </Button>
              {generateTableTab === 'payroll' && (generatedPayslips.payroll || []).length > 0 && (
                <Button
                  onClick={() => {
                    saveAllPayrollPayslipByPeriod();
                  }}
                  disabled={isSavingPayrollBatch || (generatedPayslips.payroll || []).length === 0}
                  className="bg-green-600 hover:bg-green-700 text-white"
                >
                  {isSavingPayrollBatch ? 'Saving…' : `Save (${payrollPeriod || 'Payroll'})`}
                </Button>
              )}
              <Button
                variant="outline"
                onClick={async () => {
                  if (generateTableTab === 'mf') {
                    setIsClearingMfAttendance(true);
                    setIsLoadingMfAttendance(true);
                    setMfAttendanceFilters({ name: '', start_date: '', end_date: '', type: 'all', branch: 'all' });
                    setMfAttendanceData([]);
                    setRawMfAttendanceData([]);
                    setMfAttendanceAnalytics({ total_employees: 0, days_present: 0, days_absent: 0, total_minutes_late: 0, total_minutes_undertime: 0, working_days: 0, total_gross_pay: 0, daily_rate: 0 });
                    setSelectedPayslipEmployee && setSelectedPayslipEmployee(null);
                    await new Promise((r) => setTimeout(r, 250));
                    setIsLoadingMfAttendance(false);
                    setIsClearingMfAttendance(false);
                  } else {
                    setIsClearingPayrollAttendance(true);
                    setIsLoadingPayrollAttendance(true);
                    setPayrollAttendanceFilters({ name: '', start_date: '', end_date: '', type: 'all', branch: 'all' });
                    setPayrollAttendanceData([]);
                    setRawPayrollAttendanceData([]);
                    setPayrollAttendanceAnalytics({ total_employees: 0, days_present: 0, days_absent: 0, total_minutes_late: 0, total_minutes_undertime: 0, working_days: 0, total_gross_pay: 0, daily_rate: 0 });
                    setSelectedPayslipEmployee && setSelectedPayslipEmployee(null);
                    await new Promise((r) => setTimeout(r, 250));
                    setIsLoadingPayrollAttendance(false);
                    setIsClearingPayrollAttendance(false);
                  }

                  setHolidays([]);
                  setGeneratedPayslips({ payroll: [], mf: [] });
                }}
                className="hover:bg-gray-100 dark:hover:bg-black focus:outline-none focus:ring-2 focus:ring-indigo-500"
                aria-label="Clear filters and reset table"
              >
                {(generateTableTab === 'mf' ? isClearingMfAttendance : isClearingPayrollAttendance) ? 'Clearing…' : 'Clear'}
              </Button>

            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="glass shadow-lg border-0 bg-white/90 backdrop-blur h-full min-h-0 overflow-hidden">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm">Calendar</CardTitle>
            <div className="text-xs text-gray-500 dark:text-gray-300">
              {(generateTableTab === 'mf'
                ? (mfAttendanceFilters.start_date && mfAttendanceFilters.end_date)
                : (payrollAttendanceFilters.start_date && payrollAttendanceFilters.end_date)
              )
                ? `${generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date} to ${generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date}`
                : 'Select a range'
              }
            </div>
          </div>
        </CardHeader>
        <CardContent className="min-h-0 overflow-auto">
          <div className="w-full flex justify-center">
            <CalendarComponent
              mode="range"
              numberOfMonths={computeDateRangeMonthCount(
                generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date,
                generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date
              )}
              defaultMonth={(() => {
                const s = generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date;
                const e = generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date;
                if (s) return new Date(s);
                if (e) return new Date(e);
                return new Date();
              })()}
              selected={{
                from: (generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date)
                  ? new Date(generateTableTab === 'mf' ? mfAttendanceFilters.start_date : payrollAttendanceFilters.start_date)
                  : undefined,
                to: (generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date)
                  ? new Date(generateTableTab === 'mf' ? mfAttendanceFilters.end_date : payrollAttendanceFilters.end_date)
                  : undefined
              }}
              onSelect={(range) => {
                const from = range?.from ? format(range.from, 'yyyy-MM-dd') : '';
                const to = range?.to ? format(range.to, 'yyyy-MM-dd') : (from ? from : '');
                const norm = normalizeDateRangeOrder(from, to);
                if (generateTableTab === 'mf') {
                  setMfAttendanceFilters({ ...mfAttendanceFilters, start_date: norm.start, end_date: norm.end });
                } else {
                  setPayrollAttendanceFilters({ ...payrollAttendanceFilters, name: '', start_date: norm.start, end_date: norm.end });
                }
              }}
              className="p-2"
              classNames={{
                caption_label: 'text-xs font-medium',
                head_cell: 'text-muted-foreground rounded-md w-7 font-normal text-[0.75rem]',
                day: 'h-7 w-7',
                day_selected: 'bg-green-600 text-white hover:bg-green-600 hover:text-white focus:bg-green-600 focus:text-white',
                day_range_middle: 'aria-selected:bg-green-100 aria-selected:text-green-900',
                day_today: 'bg-green-50 text-green-900'
              }}
            />
          </div>
        </CardContent>
      </Card>
    </div>

    <Card className="glass shadow-lg border-0 h-full min-h-0 overflow-hidden">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Generated Payslip List</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="h-full min-h-0 overflow-hidden">
        <div className="h-full min-h-0 flex flex-col">
          {(generatedPayslips[generateTableTab] || []).length > 0 && (
            <div className="rounded-lg border bg-white/60 p-4 flex flex-col flex-1 min-h-0">
              <div className="flex items-center justify-between">
                <div className="text-sm font-medium text-gray-900 dark:text-white">Employee Names</div>
                <div className="text-xs text-gray-600 dark:text-gray-300">{generatedPayslips[generateTableTab].length} employee(s)</div>
              </div>
              <div className="mt-3 flex-1 min-h-0 overflow-auto rounded border bg-white">
                <table className="min-w-full w-full">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="border-b">
                      <th className="text-left p-2 text-xs font-medium text-gray-700">Employee Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {generatedPayslips[generateTableTab].map((r, idx) => (
                      <tr
                        key={r.employee_id}
                        className={cn(
                          idx % 2 === 0 ? 'bg-white' : 'bg-gray-50',
                          'cursor-pointer hover:bg-indigo-50',
                          String(selectedGeneratedPayslipEmployeeId || '') === String(r.employee_id || '') && 'bg-indigo-100'
                        )}
                        onClick={() => handleGeneratedPayslipClick(r)}
                      >
                        <td className="p-2 text-sm text-gray-900">
                          <button type="button" className="text-left w-full text-indigo-700 hover:underline">
                            {r.employee_name}
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(generatedPayslips[generateTableTab] || []).length === 0 && (
            <div className="flex-1 min-h-0 text-center py-10 text-sm text-gray-600 dark:text-gray-300">
              Generate payroll to populate the list, then click an employee name to view the payslip summary.
            </div>
          )}

          <Dialog open={isGeneratedPayslipSummaryOpen} onOpenChange={setIsGeneratedPayslipSummaryOpen}>
            <DialogContent className="max-w-6xl h-[85vh] overflow-auto">
              <DialogHeader>
                <DialogTitle>Payslip Calculation Summary</DialogTitle>
                <DialogDescription>
                  {selectedGeneratedPayslipEmployeeName ? `Employee: ${selectedGeneratedPayslipEmployeeName}` : 'Employee details'}
                </DialogDescription>
              </DialogHeader>
              {renderPayslipCalculationSummary()}
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  </div>
);

export default GeneratePage;
